import IconRecycleBinThrow from "@/components/icons/icon-recycle-bin-throw";
import IconMailReply from "@/components/icons/icon-mail-reply";
import type { InviteListItemProps, UserRole } from "../types";
import { Tooltip } from "@/components/ui/tooltip";
import { getTeamPlanRestrictionMessage, TeamPlanRestrictionBolt } from "./team-plan-restriction";

function getEmailInitials(email: string) {
	const name = email.split("@")[0];
	if (name.length >= 2) {
		return name.slice(0, 2).toUpperCase();
	}
	return name.toUpperCase();
}

export default function InviteListItem({ membership, showDeleteButton = true, onDelete, onResendInvite, blockedByPlan, userRole }: InviteListItemProps) {
	const {
		id,
		user: { email },
		role,
	} = membership;

	// Check if this invite is restricted (editor role when blocked by plan)
	const isRestricted = blockedByPlan && role === "editor";
	const teamRestrictionMessage = getTeamPlanRestrictionMessage(userRole);

	const roleStyles = {
		editor: "bg-blue-50 text-blue-700 border-blue-200",
		admin: "bg-purple-50 text-purple-700 border-purple-200",
	} as const;
	const currentRoleStyle = roleStyles[role as UserRole] || roleStyles.editor;

	return (
		<div className={`group flex items-center justify-between px-5 py-3 transition-colors ${isRestricted ? "bg-gray-50" : "hover:bg-gray-50"}`}>
			<div className={`flex items-center gap-3 ${isRestricted ? "opacity-50" : ""}`}>
				<div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
					<span className="text-xs font-semibold text-amber-700">{getEmailInitials(email)}</span>
				</div>
				<div className="flex flex-col min-w-0">
					<span className="text-sm font-medium text-gray-900">{email}</span>
					<span className={`text-xs ${isRestricted ? "font-medium text-amber-700" : "text-amber-600"}`}>{isRestricted ? "Access paused on current plan" : "Pending"}</span>
				</div>
			</div>

			<div className="flex items-center gap-2">
				{isRestricted && <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-medium border border-amber-200 bg-amber-50 text-amber-700">Paused</span>}
				<span className={`inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md border ${currentRoleStyle} ${isRestricted ? "opacity-50" : ""}`}>{role.charAt(0).toUpperCase() + role.slice(1)}</span>

				{isRestricted && (
					<Tooltip content={teamRestrictionMessage}>
						<TeamPlanRestrictionBolt userRole={userRole} workspaceId={membership.workspace_id} />
					</Tooltip>
				)}

				{showDeleteButton && onDelete && (
					<>
						{!blockedByPlan && onResendInvite && (
							<Tooltip content="Resend Invite">
								<button
									onClick={() => onResendInvite(id)}
									className="p-1.5 text-gray-400 hover:text-brand hover:bg-brand/10 rounded-md transition-colors cursor-pointer"
									aria-label="Resend invite"
								>
									<IconMailReply className="w-4 h-4" />
								</button>
							</Tooltip>
						)}
						<button onClick={() => onDelete(id)} className={`p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors cursor-pointer ${isRestricted ? "opacity-50" : ""}`} aria-label="Delete invite">
							<IconRecycleBinThrow className="w-4 h-4" />
						</button>
					</>
				)}
			</div>
		</div>
	);
}
